"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useShowcase = void 0;

var tslib_1 = require("tslib");

var lodash_debounce_1 = tslib_1.__importDefault(require("lodash.debounce"));

var react_1 = require("react");

var useShowcase = function useShowcase(_a) {
  var items = _a.items,
      distanceBetweenItems = _a.distanceBetweenItems;

  var _b = tslib_1.__read((0, react_1.useState)(), 2),
      leftMostItem = _b[0],
      setLeftMostItem = _b[1];

  var _c = tslib_1.__read((0, react_1.useState)(), 2),
      rightMostItem = _c[0],
      setRightMostItem = _c[1];

  var _d = tslib_1.__read((0, react_1.useState)(false), 2),
      canScrollForward = _d[0],
      setCanScrollForward = _d[1];

  var _e = tslib_1.__read((0, react_1.useState)(false), 2),
      canScrollBackward = _e[0],
      setCanScrollBackward = _e[1];

  var contentRef = (0, react_1.useRef)(null);
  var isNewItemsRef = (0, react_1.useRef)(false);
  var checkCanScrollForward = (0, react_1.useCallback)(function () {
    var _a, _b, _c;

    var contentScrollLeft = ((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.scrollLeft) || 0;
    var contentScrollWidth = ((_b = contentRef.current) === null || _b === void 0 ? void 0 : _b.scrollWidth) || 0;
    var contentClientWidth = ((_c = contentRef.current) === null || _c === void 0 ? void 0 : _c.clientWidth) || 0;
    return contentScrollWidth - contentScrollLeft !== contentClientWidth;
  }, []);
  var checkCanScrollBackward = (0, react_1.useCallback)(function () {
    var _a;

    var contentScrollLeft = ((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.scrollLeft) || 0;
    return contentScrollLeft > distanceBetweenItems * 8;
  }, [distanceBetweenItems]);
  var scrollTo = (0, react_1.useCallback)(function (distance) {
    var _a;

    (_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo({
      left: distance,
      behavior: 'smooth'
    });
  }, []);
  var scrollForward = (0, react_1.useCallback)(function () {
    var _a;

    if (canScrollForward) {
      var offset = (rightMostItem === null || rightMostItem === void 0 ? void 0 : rightMostItem.nextSibling) ? (rightMostItem === null || rightMostItem === void 0 ? void 0 : rightMostItem.nextSibling).offsetLeft : (_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.scrollWidth;

      if (typeof offset !== 'undefined') {
        scrollTo(offset);
      }
    }
  }, [rightMostItem, canScrollForward, scrollTo]);
  var scrollBackward = (0, react_1.useCallback)(function () {
    var _a;

    if (canScrollBackward) {
      var contentClientWidth = ((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) || 0;
      var offset = ((leftMostItem === null || leftMostItem === void 0 ? void 0 : leftMostItem.offsetLeft) || 0) - contentClientWidth;
      scrollTo(offset);
    }
  }, [leftMostItem, canScrollBackward, scrollTo]);
  var handleItemEntryAndExit = (0, react_1.useCallback)(function (entries) {
    var isNewItems = isNewItemsRef.current;
    isNewItemsRef.current = false;
    entries.forEach(function (entry, index) {
      var _a;

      var nextEntry = entries[index + 1];
      var item = entry.target;

      if (isNewItems) {
        if (index === 0) {
          setLeftMostItem(item);
        }

        if (entry.isIntersecting && (nextEntry === undefined || !nextEntry.isIntersecting)) {
          setRightMostItem(item);
        }
      } else {
        // subsequent observations.
        // note if an element and another leave and enter respectively then the entries will contain 2 of them
        var previousItem = item.previousSibling || undefined;
        var nextItem = item.nextSibling || undefined;
        var contentClientWidth = ((_a = entry.rootBounds) === null || _a === void 0 ? void 0 : _a.width) || 0;
        var entryScrolledForwardOutOfView = !entry.isIntersecting && entry.boundingClientRect.x <= contentClientWidth;
        var entryScrolledForwardIntoView = entry.isIntersecting && entry.boundingClientRect.x > contentClientWidth;
        var entryScrolledBackwardOutOfView = !entry.isIntersecting && entry.boundingClientRect.x > contentClientWidth;
        var entryScrolledBackwardIntoView = entry.isIntersecting && entry.boundingClientRect.x < contentClientWidth;

        if (entryScrolledForwardOutOfView) {
          setLeftMostItem(nextItem);
        }

        if (entryScrolledForwardIntoView) {
          setRightMostItem(item);
        }

        if (entryScrolledBackwardOutOfView) {
          setRightMostItem(previousItem);
        }

        if (entryScrolledBackwardIntoView) {
          setLeftMostItem(item);
        }
      }
    });
  }, []);
  var itemCount = react_1.Children.count(items);
  (0, react_1.useLayoutEffect)(function () {
    var _a;

    isNewItemsRef.current = true;
    setLeftMostItem(undefined);
    setRightMostItem(undefined);
    setCanScrollForward(checkCanScrollForward());
    var intersectionObserver = new IntersectionObserver(handleItemEntryAndExit, {
      root: contentRef.current
    });
    Array.from(((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.children) || []).forEach(function (item) {
      intersectionObserver.observe(item);
    });
    return function () {
      return intersectionObserver.disconnect();
    };
  }, [checkCanScrollForward, handleItemEntryAndExit, itemCount]);
  var setCanScroll = (0, lodash_debounce_1.default)(function () {
    setCanScrollForward(checkCanScrollForward());
    setCanScrollBackward(checkCanScrollBackward());
  }, 100);
  var handleOnResize = (0, react_1.useCallback)(setCanScroll, [setCanScroll]);
  (0, react_1.useLayoutEffect)(function () {
    var resizeObserver = new ResizeObserver(handleOnResize);
    resizeObserver.observe(contentRef === null || contentRef === void 0 ? void 0 : contentRef.current);
    return function () {
      return resizeObserver.disconnect();
    };
  }, [handleOnResize]);
  var handleOnScroll = (0, react_1.useCallback)(setCanScroll, [setCanScroll]);
  (0, react_1.useEffect)(function () {
    var current = contentRef.current;
    current === null || current === void 0 ? void 0 : current.addEventListener('scroll', handleOnScroll);
    return function () {
      return current === null || current === void 0 ? void 0 : current.removeEventListener('scroll', handleOnScroll);
    };
  }, [handleOnScroll]);
  return {
    canScrollForward: canScrollForward,
    canScrollBackward: canScrollBackward,
    contentRef: contentRef,
    scrollForward: scrollForward,
    scrollBackward: scrollBackward
  };
};

exports.useShowcase = useShowcase;